import { isAfter } from "date-fns";
import { capitalize } from "lodash";
import { FC, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useCompanyContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import {
  dataMetaSelector,
  getCompanySubscriptions,
  getPaymentInfo,
  paymentInfoSelector,
} from "Bands/state";
import {
  BAND_ACTIONS,
  BillingStatus,
  IBandSubscription,
  SubscriptionStatus,
} from "Bands/types";
import { useBandRoute } from "Bands/utils";
import { copy } from "shared/assets";
import {
  IActionDropdownItem,
  Table,
  TableColumn,
  TableSkeleton,
} from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { MutedDarkText, MutedText, SmallText } from "shared/styles";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { formatCell, formatDateTime, mapUserPermissions } from "shared/utils";

const subscriptionsCopy = copy.companyDashboard.subscriptions;

interface SubscriptionsTableProps {
  setShowCancelSubscriptionModal: (subscriptionId: string) => void;
}

export const SubscriptionsTable: FC<SubscriptionsTableProps> = ({
  setShowCancelSubscriptionModal,
}) => {
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getCompanySubscriptions)
  );

  const { companySubscriptions, companyPath, companyId } = useCompanyContext();
  const dispatch = useDispatch();
  const { pushRoute } = useBandRoute();

  const paymentInfo = useSelector(paymentInfoSelector) as unknown as {
    [key: string]: { billingPortalUrl: string };
  };

  useEffect(() => {
    if (companyPath) {
      dispatch(
        getCompanySubscriptions({
          company: companyPath,
          status: SubscriptionStatus.active,
        })
      );

      dispatch(
        getPaymentInfo({
          id: companyId!,
          redirectUrl: window.location.href,
        })
      );
    }
  }, [companyPath, companyId, dispatch]);

  const getRowActions = useCallback(
    (subscriptionId: string) => {
      const subscription = companySubscriptions.find(
        ({ id }) => id === subscriptionId
      );

      if (!subscription) return [];

      const permissions = mapUserPermissions(subscription.permissions || []);

      let actions: IActionDropdownItem[] = [];

      if (permissions.CHANGE_PLAN) {
        actions.push({
          label: subscriptionsCopy.actions.editPayment,
          handleClick: () => {
            window.location.href =
              paymentInfo[
                API_ENDPOINTS.SUBSCRIPTION(subscriptionId)
              ].billingPortalUrl;
          },
          dataTestId: "edit_payment_action",
        });

        actions.push({
          label: subscriptionsCopy.actions.editSubscription,
          handleClick: () =>
            pushRoute(BAND_PATHS.EDIT_SUBSCRIPTION_PLAN, {
              bandId: subscription?.bands[0].id,
            }),
          dataTestId: "edit_subscription_action",
        });
      }

      if (permissions.CANCEL) {
        actions.push({
          label: subscriptionsCopy.actions.cancelSubscription,
          dataTestId: "cancel_subscription_action",
          handleClick: () => setShowCancelSubscriptionModal(subscriptionId),
          status: IStatus.error,
        });
      }

      return actions;
    },
    [
      pushRoute,
      companySubscriptions,
      setShowCancelSubscriptionModal,
      paymentInfo,
    ]
  );

  if (apiMeta?.status === IAPIState.pending)
    return <TableSkeleton theme={ITheme.tertiary} />;

  return (
    <Table
      columns={columns}
      data={companySubscriptions}
      emptyCopy={subscriptionsCopy.empty}
      getRowActions={getRowActions}
    />
  );
};

const columns: TableColumn[] = [
  { Header: subscriptionsCopy.columns.band, accessor: "bands[0].title" },
  {
    Header: subscriptionsCopy.columns.type,
    accessor: "plan",
    Cell: ({ value, row }) => {
      const subscription = row.original as IBandSubscription;
      const plan = capitalize(value);

      let startsOn = null;
      let until = null;
      let upcoming = null;

      if (
        subscription.start &&
        isAfter(new Date(subscription.start), Date.now())
      ) {
        startsOn = (
          <MutedDarkText as={SmallText}>
            starts on{" "}
            {formatDateTime(subscription.start, {
              dateOnly: true,
            })}
          </MutedDarkText>
        );
      }

      if (
        subscription.billingStatus ===
          BillingStatus.not_automatically_renewed &&
        subscription.stop
      ) {
        until = (
          <MutedDarkText as={SmallText}>
            until{" "}
            {formatDateTime(subscription.stop, {
              dateOnly: true,
            })}
          </MutedDarkText>
        );
      }

      if (subscription.nextPhasePlan && subscription.nextPhaseStart) {
        upcoming = (
          <MutedText>
            {capitalize(subscription.nextPhasePlan)} from{" "}
            {formatDateTime(subscription.nextPhaseStart, {
              dateOnly: true,
            })}
          </MutedText>
        );
      }

      return (
        <Spacer size="xxs">
          <span>
            {plan} {startsOn} {until}
          </span>
          {upcoming}
        </Spacer>
      );
    },
  },
  {
    Header: subscriptionsCopy.columns.nextBillingDate,
    accessor: "nextBillingDate",
    Cell: ({ value }) =>
      value ? formatDateTime(value, { dateOnly: true }) : formatCell(),
  },
  {
    Header: subscriptionsCopy.columns.by,
    accessor: "createdBy",
    Cell: ({ value }) => `${value.firstname} ${value.lastname}`,
  },
  {
    Header: subscriptionsCopy.columns.payment,
    accessor: "paymentMethod",
    Cell: ({ value }) => formatCell(value),
  },
];
